// @flow
import React, { Component } from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from 'trpc-react-native-ui';
import type { Subscription } from 'digicel-kaizen-library';

import Frame from '../../common/Frame';
import PlanSummary from '../../common/PlanSummary';
import theme from '../../../theme-config';
import I18n from '../../../lib/i18n';
import FormatService from '../../../lib/format';
import { getFormattedOptionsWithDefault, getSecondaryCharacteristicsForOptions } from '../../../lib/optionsFormatter';

class PlanSummaryFrame extends Component<Props, State> {
  state = { isPressed: false };

  _touchStartX = 0;
  _touchStartY = 0;
  _isTouchCanceled = false;
  static TAP_MOVE_THRESHOLD = 10;

  _onTouchStart = (e) => {
    this._touchStartX = e.nativeEvent.pageX;
    this._touchStartY = e.nativeEvent.pageY;
    this._isTouchCanceled = false;
    this.setState({ isPressed: true });
  };

  _onTouchMove = (e) => {
    if (this._isTouchCanceled) return;
    const dx = Math.abs(e.nativeEvent.pageX - this._touchStartX);
    const dy = Math.abs(e.nativeEvent.pageY - this._touchStartY);
    if (dx > PlanSummaryFrame.TAP_MOVE_THRESHOLD || dy > PlanSummaryFrame.TAP_MOVE_THRESHOLD) {
      this._isTouchCanceled = true;
      this.setState({ isPressed: false });
    }
  };

  _onTouchEnd = () => {
    const wasCanceled = this._isTouchCanceled;
    this.setState({ isPressed: false });
    if (!wasCanceled) this.onPress();
  };

  _onTouchCancel = () => {
    this._isTouchCanceled = true;
    this.setState({ isPressed: false });
  };

  getPortabilityNumber = (subscription?: Subscription): ?string => subscription?.portability?.characteristics?.MSISDN;

  onPress = () => {
    const { onPress, setSubscriptionToUpdate, subscription } = this.props;
    if (setSubscriptionToUpdate) {
      if (subscription.hasUnavailableOffers) {
        setSubscriptionToUpdate(subscription.ouid);
      } else {
        setSubscriptionToUpdate();
      }
    }

    onPress?.(subscription);
  };

  getPortabilityStatus = (status: string, orderState: ?string): string => {
    switch (status) {
      case 'CREATED':
        if (orderState === 'IN_PROGRESS') {
          return I18n.t('Dashboard.Orders.portabilityStatus.inProgress');
        }
        return I18n.t('Dashboard.Orders.portabilityStatus.requested');
      case 'PENDING_ACTIVE':
        return I18n.t('Dashboard.Orders.portabilityStatus.inProgress');
      case 'ACTIVE':
      default:
        return I18n.t('Dashboard.Orders.portabilityStatus.active');
    }
  };
  getPortabilityStatusStyle = (status: string): ?any => status === 'PENDING_ACTIVE' && { color: theme.secondaryColor };

  getTitleTranslationPath = (status: string): string =>
    status === 'PENDING_ACTIVE' ? 'Dashboard.PlanSummaryFrame.pendingActiveTitle' : 'Dashboard.PlanSummaryFrame.title';

  render() {
    const {
      subscription,
      showPortabilityStatus,
      showTitle,
      showSubscriptionsWithoutPhoneNumber,
      isActivation,
    } = this.props;

    let isPortabilityActivation = isActivation && subscription.portability;

    let phoneNumber = subscription.line.number || this.getPortabilityNumber(subscription);
    if (isPortabilityActivation) {
      phoneNumber = this.getPortabilityNumber(subscription);
    }

    if (!showSubscriptionsWithoutPhoneNumber && !phoneNumber) return null;

    const line = phoneNumber
      ? FormatService.formatPhone(phoneNumber, !!subscription.portability)
      : I18n.t('SignUp.SelectSubscriptionToActivatePage.newPlanFrame');

    return (
      <View
        onTouchStart={this._onTouchStart}
        onTouchMove={this._onTouchMove}
        onTouchEnd={this._onTouchEnd}
        onTouchCancel={this._onTouchCancel}
        style={this.state.isPressed && styles.pressed}>
        <Frame style={[styles.container, this.props.isHighlighted && styles.isHighlighted, this.props.style]}>
          {showTitle && <Text style={styles.title}>{I18n.t(this.getTitleTranslationPath(subscription.status))}</Text>}
          <Text style={styles.line}>
            {line}
            {isPortabilityActivation && <Text>{` (${I18n.t('Dashboard.Orders.portability')})`}</Text>}
          </Text>
          {showPortabilityStatus && subscription.portability && subscription.status && (
            <Text style={[styles.line, styles.status, this.getPortabilityStatusStyle(subscription.status)]}>
              {this.getPortabilityStatus(subscription.status, subscription.orderState)}
            </Text>
          )}
          <View style={styles.planSummaryContainer}>
            <PlanSummary
              options={getFormattedOptionsWithDefault(subscription.options)}
              secondaryCharacteristicsOptions={getSecondaryCharacteristicsForOptions(subscription.options)}
              doSubscriptionOptionsIncludeUnavailableOption={subscription.hasUnavailableOffers}
              unavailablePlanText={this.props.unavailablePlanText || I18n.t('Dashboard.Plan.unavailablePlanOnPickLine')}
              fromDashboard={this.props.fromDashboard}
              isActivation={isActivation}
            />
          </View>
        </Frame>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    paddingVertical: 10,
    paddingHorizontal: 16,
  },
  frameContent: {
    flexDirection: 'row',
  },
  title: {
    textAlign: 'center',
    fontSize: 16,
    marginVertical: 5,
  },
  line: {
    textAlign: 'center',
    fontSize: 16,
    fontWeight: '500',
    marginVertical: 4,
  },
  status: {
    marginVertical: 0,
  },
  numberPortability: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  planSummaryContainer: {
    marginTop: 20,
  },
  isHighlighted: {
    backgroundColor: theme.selectedBackground,
  },
  pressed: {
    opacity: 0.7,
  },
});

type Props = {
  subscription: Subscription,
  isFetching: boolean,
  style?: any,
  onPress?: (subscription: Subscription) => void,
  showPortabilityNextToNumber?: boolean,
  showPortabilityStatus?: boolean,
  showTitle?: boolean,
  showSubscriptionsWithoutPhoneNumber?: boolean,
  isHighlighted?: boolean,
  isActivation?: boolean,
  setSubscriptionToUpdate?: Function,
  unavailablePlanText?: string,
  fromDashboard: boolean,
};

type State = {
  isPressed: boolean,
};

export default PlanSummaryFrame;
